import React, { useEffect, useMemo, useState } from "react";

/**
 * Component 2 — lightweight UI tooltip tour (no external tour library).
 * Spotlights key surfaces; on finish/dismiss the caller invokes
 * NotifyTourComplete on the onboarding hub so the flow advances to the value-demo step.
 */
const TOUR_STEPS = [
  {
    selectors: [],
    title: "This is your chat with Mila",
    text: "Type any question about your fleet in plain language — drivers, vehicles, safety events, trends — and I'll answer with text, tables and charts.",
  },
  {
    selectors: ["[data-tour='new-chat']"],
    title: "Start a new conversation",
    text: 'Click "New Chat" any time to ask about something else. Each conversation is saved so you can come back to it.',
  },
  {
    selectors: ["[data-tour='history']"],
    title: "Your history & favorites",
    text: "Every conversation lands here. Star the answers you love to pin them under Favorites.",
  },
  {
    selectors: ["[data-tour='insights-toggle']", "[data-tour='insights']"],
    title: "Scheduled insights",
    text: "You can schedule questions to run automatically. Click this button on the right edge to open the list and read the answers as they arrive.",
  },
];

// Collapsed or hidden targets (the closed insights panel is a few pixels wide) get no spotlight.
const MIN_TARGET_SIZE = 24;
const CARD_WIDTH = 340;
const CARD_HEIGHT = 180;
const MARGIN = 12;

const OnboardingTour = ({ onComplete }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [rects, setRects] = useState([]);

  const step = TOUR_STEPS[stepIndex];

  useEffect(() => {
    const measure = () => {
      const found = (step?.selectors || [])
        .map((selector) => document.querySelector(selector))
        .filter(Boolean)
        .map((el) => el.getBoundingClientRect())
        .filter((r) => r.width >= MIN_TARGET_SIZE && r.height >= MIN_TARGET_SIZE)
        .map((r) => ({ top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height }));
      setRects(found);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [step]);

  // Single box enclosing every spotlight of the step — used only to place the card.
  const anchor = useMemo(() => {
    if (!rects.length) return null;
    const top = Math.min(...rects.map((r) => r.top));
    const left = Math.min(...rects.map((r) => r.left));
    const right = Math.max(...rects.map((r) => r.right));
    const bottom = Math.max(...rects.map((r) => r.bottom));
    return { top, left, right, bottom, width: right - left, height: bottom - top };
  }, [rects]);

  const cardStyle = useMemo(() => {
    if (!anchor) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }
    // A full-height target (the insights column) leaves no room above or below it.
    if (anchor.height + CARD_HEIGHT + MARGIN * 2 > window.innerHeight) {
      const leftSide = anchor.left - CARD_WIDTH - MARGIN;
      const left = leftSide >= MARGIN ? leftSide : Math.min(anchor.right + MARGIN, window.innerWidth - CARD_WIDTH - MARGIN);
      const top = Math.min(
        Math.max(MARGIN, anchor.top + anchor.height / 2 - CARD_HEIGHT / 2),
        Math.max(MARGIN, window.innerHeight - CARD_HEIGHT - MARGIN)
      );
      return { top, left: Math.max(MARGIN, left) };
    }
    let top = anchor.bottom + MARGIN;
    let left = anchor.left;
    if (top + CARD_HEIGHT > window.innerHeight) top = Math.max(MARGIN, anchor.top - CARD_HEIGHT - MARGIN);
    if (left + CARD_WIDTH > window.innerWidth) left = Math.max(MARGIN, window.innerWidth - CARD_WIDTH - MARGIN);
    return { top, left };
  }, [anchor]);

  const isLast = stepIndex === TOUR_STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[60] font-notosans">
      {/* dim layer, punched through for every target of the step */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none">
        <defs>
          <mask id="mila-tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {rects.map((r, i) => (
              <rect
                key={i}
                x={r.left - 6}
                y={r.top - 6}
                width={r.width + 12}
                height={r.height + 12}
                rx="16"
                fill="black"
              />
            ))}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="black" fillOpacity="0.5" mask="url(#mila-tour-mask)" />
      </svg>

      {/* spotlight ring around each target */}
      {rects.map((r, i) => (
        <div
          key={i}
          className="absolute rounded-2xl border-2 border-white pointer-events-none"
          style={{
            top: r.top - 6,
            left: r.left - 6,
            width: r.width + 12,
            height: r.height + 12,
            backgroundColor: "transparent",
          }}
        />
      ))}

      {/* step card */}
      <div
        className="absolute bg-white rounded-2xl shadow-2xl p-5 w-[340px] max-w-[90vw]"
        style={cardStyle}
      >
        <div className="text-[11px] font-semibold tracking-wider text-dimgray-200 uppercase mb-1">
          Quick tour · {stepIndex + 1}/{TOUR_STEPS.length}
        </div>
        <h3 className="text-dark-blue-100 font-bold text-[16px] mb-2">{step.title}</h3>
        <p className="text-dimgray-200 text-[13px] leading-5 mb-4">{step.text}</p>
        <div className="flex items-center justify-between">
          <button
            onClick={onComplete}
            className="text-dimgray-200 text-[12px] underline hover:text-dark-blue-100"
          >
            Skip tour
          </button>
          <button
            onClick={() => (isLast ? onComplete() : setStepIndex((i) => i + 1))}
            className="bg-dark-blue-100 hover:bg-dark-blue-hover text-white text-[13px] font-bold rounded-[14px] px-5 py-2"
          >
            {isLast ? "Finish" : "Next"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
